import { api } from "@/lib/axios";
import type { ApiResponse } from "../types/company.types";
import type {
  DispatchCarSpecItem,
  UpsertDispatchCarSpecRequest,
} from "../types/carSpec.types";

export const getDispatchCarSpecs = async (
  dispatchCompanyGuid: string,
): Promise<DispatchCarSpecItem[]> => {
  const { data } = await api.get<ApiResponse<DispatchCarSpecItem[]>>(
    "/api/Etc/DispatchCarSpecs",
    { params: { dispatchCompanyGuid } },
  );
  if (data.code !== 200) throw new Error(data.message);
  return data.result ?? [];
};

export const upsertDispatchCarSpec = async (
  authGuid: string,
  req: UpsertDispatchCarSpecRequest,
): Promise<boolean> => {
  const { data } = await api.post<ApiResponse<boolean>>(
    "/api/Etc/UpsertDispatchCarSpec",
    req,
    { params: { authGuid } },
  );
  if (data.code !== 200) throw new Error(data.message);
  return data.result;
};

/** 소프트 삭제 — 화물·단가에 남은 톤수 문자열은 유지된다. */
export const deleteDispatchCarSpec = async (
  authGuid: string,
  dispatchCompanyGuid: string,
  guid: string,
): Promise<boolean> => {
  const { data } = await api.post<ApiResponse<boolean>>(
    "/api/Etc/DeleteDispatchCarSpec",
    { DispatchCompanyGuid: dispatchCompanyGuid, Guid: guid },
    { params: { authGuid } },
  );
  if (data.code !== 200) throw new Error(data.message);
  return data.result;
};

/**
 * 표준 차량 스펙의 차종·차체옵션 어휘.
 *
 * 커스텀 톤수는 톤수만 새로 만들고 차종·옵션은 기존 어휘를 재사용한다 — 주선사마다 이름이
 * 갈리면 정보망 매핑·엑셀 파서·통계가 전부 어긋나기 때문. 서버도 같은 규칙으로 검증하므로
 * 이 목록에 없는 값을 보내면 400이 떨어진다.
 *
 * SupportCarSpecs(가공본)가 아니라 SupportCarSpecsDetail(원본 블롭)을 쓴다 —
 * 가공본은 직교 옵션(광폭 등)을 표시용으로 carBodyOptions에 합쳐 내려주는데, 그걸 그대로
 * 쓰면 조합형 옵션이 배타 옵션으로 둔갑한다.
 *
 * 옵션은 표준에서 (톤수,차종)별로 갈리지만 커스텀 톤수엔 대응하는 표준 톤수가 없으므로
 * 차종 기준 합집합을 쓴다.
 */
export interface CommonCarTypeCatalogItem {
  name: string;
  carBodyOptions: string[];
}

export const getCommonCarTypeCatalog = async (): Promise<CommonCarTypeCatalogItem[]> => {
  const { data } = await api.get<
    ApiResponse<{
      supportCarSpecs: Array<{
        carTypes: Array<{ name: string; carBodyOptions: Array<{ name: string }> }>;
      }>;
    }>
  >("/api/Etc/SupportCarSpecsDetail");
  if (data.code !== 200) throw new Error(data.message);

  const optionsByType = new Map<string, Set<string>>();
  for (const spec of data.result.supportCarSpecs) {
    for (const type of spec.carTypes) {
      if (!type.name) continue;
      const options = optionsByType.get(type.name) ?? new Set<string>();
      for (const opt of type.carBodyOptions ?? []) {
        // 이름 없는 옵션은 "옵션 없음" 기본 스펙이라 선택지가 아니다.
        if (opt.name) options.add(opt.name);
      }
      optionsByType.set(type.name, options);
    }
  }

  return [...optionsByType].map(([name, options]) => ({
    name,
    carBodyOptions: [...options].sort(),
  }));
};
